import React, { useState, useEffect, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ArrowLeft, Plus, Trash2, Package, Check, Clock, Truck } from "lucide-react";

const UNITS = ["pieces", "sqft", "linear_ft", "boxes", "sheets", "gallons", "lbs"];
const STATUS_MAP = {
  planned: { label: "Planned", color: "bg-slate-100 text-slate-600", icon: Clock },
  ordered: { label: "Ordered", color: "bg-blue-100 text-blue-700", icon: Truck },
  partial: { label: "Partial", color: "bg-yellow-100 text-yellow-700", icon: Package },
  received: { label: "Received", color: "bg-green-100 text-green-700", icon: Check },
};

export default function MaterialsPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ materials: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newMat, setNewMat] = useState({ project_id: "", name: "", quantity_ordered: 0, quantity_received: 0, quantity_used: 0, unit: "pieces", unit_cost: 0, supplier: "", notes: "" });

  useEffect(() => {
    setNewMat(m => ({ ...m, project_id: projectId }));
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/materials/${projectId}`);
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load materials");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newMat.name) { toast.error("Enter material name"); return; }
    try {
      await axios.post(`${API}/materials`, { ...newMat, project_id: projectId });
      toast.success("Material added");
      setShowAdd(false);
      setNewMat({ project_id: projectId, name: "", quantity_ordered: 0, quantity_received: 0, quantity_used: 0, unit: "pieces", unit_cost: 0, supplier: "", notes: "" });
      fetchData();
    } catch (err) {
      toast.error("Failed to add");
    }
  };

  const handleUpdate = async (materialId, field, value) => {
    try {
      await axios.put(`${API}/materials/${materialId}`, { [field]: value });
      fetchData();
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  const handleDelete = async (materialId) => {
    if (!window.confirm("Delete this material?")) return;
    try {
      await axios.delete(`${API}/materials/${materialId}`);
      fetchData();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const s = data.summary;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="materials-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Package className="w-6 h-6 text-blue-600" /> Materials Tracker</h1>
          <p className="text-slate-600 mt-1">{s.total_items || 0} items | ${(s.total_cost || 0).toLocaleString()} total | {s.pending_delivery || 0} awaiting delivery</p>
        </div>
        <Button onClick={() => setShowAdd(true)} data-testid="add-material-btn"><Plus className="w-4 h-4 mr-2" /> Add Material</Button>
      </div>

      {/* Materials List */}
      <Card>
        <CardContent className="p-0">
          {data.materials.length > 0 ? (
            <table className="data-table w-full">
              <thead>
                <tr><th>Material</th><th>Supplier</th><th>Ordered</th><th>Received</th><th>Used</th><th>Unit Cost</th><th>Total</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {data.materials.map(mat => {
                  const st = STATUS_MAP[mat.status] || STATUS_MAP.planned;
                  const StatusIcon = st.icon;
                  return (
                    <tr key={mat.material_id} data-testid={`mat-${mat.material_id}`}>
                      <td className="font-medium">{mat.name}<br /><span className="text-xs text-slate-400">{mat.unit}</span></td>
                      <td className="text-sm text-slate-500">{mat.supplier || "—"}</td>
                      <td>
                        <Input type="number" className="w-16 h-7 text-xs" value={mat.quantity_ordered}
                          onChange={(e) => handleUpdate(mat.material_id, "quantity_ordered", parseFloat(e.target.value) || 0)} />
                      </td>
                      <td>
                        <Input type="number" className="w-16 h-7 text-xs" value={mat.quantity_received}
                          onChange={(e) => handleUpdate(mat.material_id, "quantity_received", parseFloat(e.target.value) || 0)} />
                      </td>
                      <td>
                        <Input type="number" className="w-16 h-7 text-xs" value={mat.quantity_used}
                          onChange={(e) => handleUpdate(mat.material_id, "quantity_used", parseFloat(e.target.value) || 0)} />
                      </td>
                      <td className="text-sm">${mat.unit_cost.toFixed(2)}</td>
                      <td className="font-semibold">${(mat.unit_cost * mat.quantity_ordered).toLocaleString()}</td>
                      <td><Badge className={st.color}><StatusIcon className="w-3 h-3 mr-1 inline" />{st.label}</Badge></td>
                      <td><Button variant="ghost" size="icon" className="h-7 w-7 text-red-400" onClick={() => handleDelete(mat.material_id)}><Trash2 className="w-3 h-3" /></Button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12"><Package className="w-10 h-10 text-slate-300 mx-auto mb-3" /><p className="text-slate-500">No materials tracked yet</p></div>
          )}
        </CardContent>
      </Card>

      {/* Add Modal */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
          <DialogHeader><DialogTitle>Add Material</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Material Name *</Label><Input value={newMat.name} onChange={(e) => setNewMat({ ...newMat, name: e.target.value })} placeholder="e.g., Shaker Door Panels" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Unit</Label>
                <Select value={newMat.unit} onValueChange={(v) => setNewMat({ ...newMat, unit: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Unit Cost ($)</Label><Input type="number" value={newMat.unit_cost} onChange={(e) => setNewMat({ ...newMat, unit_cost: parseFloat(e.target.value) || 0 })} /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Ordered</Label><Input type="number" value={newMat.quantity_ordered} onChange={(e) => setNewMat({ ...newMat, quantity_ordered: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Received</Label><Input type="number" value={newMat.quantity_received} onChange={(e) => setNewMat({ ...newMat, quantity_received: parseFloat(e.target.value) || 0 })} /></div>
              <div><Label>Used</Label><Input type="number" value={newMat.quantity_used} onChange={(e) => setNewMat({ ...newMat, quantity_used: parseFloat(e.target.value) || 0 })} /></div>
            </div>
            <div><Label>Supplier</Label><Input value={newMat.supplier} onChange={(e) => setNewMat({ ...newMat, supplier: e.target.value })} placeholder="Supplier name" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button><Button onClick={handleAdd}>Add Material</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
